import React, { useState, useEffect } from 'react';
import {
    DocumentIcon,
    CalendarIcon,
    BuildingOfficeIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    ArrowDownTrayIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    CheckCircleIcon,
    FolderIcon,
    ChevronLeftIcon
} from '@heroicons/react/24/outline';
import { FolderOpenIcon } from '@heroicons/react/24/solid';
import { apiUrl } from '../../utils/api';

interface Building {
    _id: string;
    nombre: string;
    ciudad: string;
}

interface User {
    _id: string;
    nombre: string;
    apellido: string;
    email: string;
}

interface Document {
    _id: string;
    nombre: string;
    descripcion: string;
    etiqueta: string;
    tipo: 'financiero' | 'legal' | 'contable' | 'comunicado' | 'general';
    fechaCaducidad: string | null;
    edificios: Building[];
    usuarios: User[];
    archivo: {
        nombreOriginal: string;
        nombreArchivo: string;
        ruta: string;
        tamaño: number;
        tipoMime: string;
    };
    creadoPor: {
        _id: string;
        nombre: string;
        apellido: string;
        email: string;
    };
    activo: boolean;
    createdAt: string;
    updatedAt: string;
}

type TipoDocumento = 'financiero' | 'legal' | 'contable' | 'comunicado' | 'general';

const TIPOS_ORDEN: TipoDocumento[] = ['financiero', 'legal', 'contable', 'comunicado', 'general'];

const TIPO_LABELS: Record<TipoDocumento, string> = {
    financiero: 'Financiero',
    legal: 'Legal',
    contable: 'Contable',
    comunicado: 'Comunicado',
    general: 'General',
};

const Documents: React.FC = () => {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterTag, setFilterTag] = useState<string>('');
    const [selectedFolder, setSelectedFolder] = useState<TipoDocumento | null>(null);

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl('/api/documents/user'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setDocuments(data.data.documents);
            } else {
                console.error('Error al obtener documentos');
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (documentId: string, nombreOriginal: string) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl(`/api/documents/${documentId}/download`), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = nombreOriginal;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                const error = await response.json();
                alert(error.message || 'Error al descargar documento');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al descargar documento');
        }
    };

    const getTypeColor = (tipo: string) => {
        switch (tipo) {
            case 'financiero': return 'bg-green-100 text-green-800';
            case 'legal': return 'bg-blue-100 text-blue-800';
            case 'contable': return 'bg-purple-100 text-purple-800';
            case 'comunicado': return 'bg-yellow-100 text-yellow-800';
            case 'general': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getFolderColors = (tipo: string) => {
        switch (tipo) {
            case 'financiero': return { folder: 'text-green-500', bg: 'bg-green-50 hover:bg-green-100 border-green-200', badge: 'bg-green-100 text-green-700' };
            case 'legal': return { folder: 'text-blue-500', bg: 'bg-blue-50 hover:bg-blue-100 border-blue-200', badge: 'bg-blue-100 text-blue-700' };
            case 'contable': return { folder: 'text-purple-500', bg: 'bg-purple-50 hover:bg-purple-100 border-purple-200', badge: 'bg-purple-100 text-purple-700' };
            case 'comunicado': return { folder: 'text-yellow-500', bg: 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200', badge: 'bg-yellow-100 text-yellow-700' };
            case 'general': return { folder: 'text-gray-500', bg: 'bg-gray-50 hover:bg-gray-100 border-gray-200', badge: 'bg-gray-100 text-gray-700' };
            default: return { folder: 'text-gray-500', bg: 'bg-gray-50 hover:bg-gray-100 border-gray-200', badge: 'bg-gray-100 text-gray-700' };
        }
    };

    const getTypeIcon = (tipo: string) => {
        switch (tipo) {
            case 'financiero': return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
            case 'legal': return <InformationCircleIcon className="h-6 w-6 text-blue-500" />;
            case 'contable': return <InformationCircleIcon className="h-6 w-6 text-purple-500" />;
            case 'comunicado': return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />;
            case 'general': return <DocumentIcon className="h-6 w-6 text-gray-500" />;
            default: return <DocumentIcon className="h-6 w-6 text-gray-500" />;
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const isExpired = (fechaCaducidad: string | null) => {
        if (!fechaCaducidad) return false;
        return new Date(fechaCaducidad) < new Date();
    };

    // Carpetas: tipos que tienen al menos un documento
    const foldersWithDocs = React.useMemo(() => {
        const counts: Partial<Record<TipoDocumento, number>> = {};
        documents.forEach(doc => {
            counts[doc.tipo] = (counts[doc.tipo] ?? 0) + 1;
        });
        return TIPOS_ORDEN.filter(tipo => (counts[tipo] ?? 0) > 0).map(tipo => ({
            tipo,
            count: counts[tipo] ?? 0,
        }));
    }, [documents]);

    // Etiquetas disponibles dentro de la carpeta seleccionada (o todas si no hay carpeta)
    const tagOptions = React.useMemo(() => {
        const base = selectedFolder ? documents.filter(d => d.tipo === selectedFolder) : documents;
        const seen = new Set<string>();
        const tags: string[] = [];
        base.forEach(doc => {
            if (doc.etiqueta && !seen.has(doc.etiqueta)) {
                seen.add(doc.etiqueta);
                tags.push(doc.etiqueta);
            }
        });
        return tags;
    }, [documents, selectedFolder]);

    // Documentos filtrados dentro de la carpeta activa
    const filteredDocuments = React.useMemo(() => {
        if (!selectedFolder) return [];
        return documents.filter(doc => {
            if (doc.tipo !== selectedFolder) return false;
            const matchesSearch = !searchTerm ||
                doc.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                doc.descripcion.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesTag = !filterTag || doc.etiqueta === filterTag;
            return matchesSearch && matchesTag;
        });
    }, [documents, selectedFolder, searchTerm, filterTag]);

    const handleOpenFolder = (tipo: TipoDocumento) => {
        setSelectedFolder(tipo);
        setSearchTerm('');
        setFilterTag('');
    };

    const handleBack = () => {
        setSelectedFolder(null);
        setSearchTerm('');
        setFilterTag('');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Portal Inversionistas</h1>
                <h2 className="text-2xl font-semibold text-gray-600">Documentos</h2>
                <p className="text-gray-600 mt-2">Accede a los documentos compartidos por la administración</p>
            </div>

            {/* Vista raíz: carpetas */}
            {!selectedFolder && (
                <>
                    {foldersWithDocs.length === 0 ? (
                        <div className="bg-white rounded-lg shadow p-12 text-center">
                            <FolderIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay documentos</h3>
                            <p className="text-gray-500">No se encontraron documentos disponibles en este momento.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {foldersWithDocs.map(({ tipo, count }) => {
                                const colors = getFolderColors(tipo);
                                return (
                                    <button
                                        key={tipo}
                                        onClick={() => handleOpenFolder(tipo)}
                                        className={`flex flex-col items-center justify-center p-6 rounded-xl border-2 transition-all cursor-pointer text-center ${colors.bg}`}
                                    >
                                        <FolderOpenIcon className={`h-14 w-14 mb-3 ${colors.folder}`} />
                                        <span className="text-sm font-semibold text-gray-800">{TIPO_LABELS[tipo]}</span>
                                        <span className={`mt-2 text-xs font-medium px-2 py-0.5 rounded-full ${colors.badge}`}>
                                            {count} {count === 1 ? 'documento' : 'documentos'}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {/* Vista interna: archivos de la carpeta seleccionada */}
            {selectedFolder && (
                <>
                    {/* Breadcrumb / header de carpeta */}
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={handleBack}
                            className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 font-medium transition-colors"
                        >
                            <ChevronLeftIcon className="h-5 w-5" />
                            <span>Volver</span>
                        </button>
                        <span className="text-gray-400">/</span>
                        <div className="flex items-center space-x-2">
                            <FolderOpenIcon className={`h-5 w-5 ${getFolderColors(selectedFolder).folder}`} />
                            <span className="font-semibold text-gray-800">{TIPO_LABELS[selectedFolder]}</span>
                        </div>
                    </div>

                    {/* Filtros dentro de carpeta */}
                    <div className="bg-white p-4 rounded-lg shadow">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
                                <div className="relative">
                                    <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Buscar documentos..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Etiqueta</label>
                                <select
                                    value={filterTag}
                                    onChange={(e) => setFilterTag(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">Todas las etiquetas</option>
                                    {tagOptions.map((tag) => (
                                        <option key={tag} value={tag}>{tag}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Lista de documentos */}
                    <div className="space-y-4">
                        {filteredDocuments.length === 0 ? (
                            <div className="bg-white rounded-lg shadow p-12 text-center">
                                <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No hay documentos</h3>
                                <p className="text-gray-500">No se encontraron documentos con los filtros actuales.</p>
                            </div>
                        ) : (
                            filteredDocuments.map((document) => (
                                <div key={document._id} className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-3">
                                                {getTypeIcon(document.tipo)}
                                                <div>
                                                    <div className="flex items-center space-x-2">
                                                        <h3 className="text-lg font-semibold text-gray-900">{document.nombre}</h3>
                                                        {document.etiqueta && (
                                                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                                                                {document.etiqueta}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-gray-600">{document.descripcion}</p>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-4">
                                                <div className="flex items-center space-x-2">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(document.tipo)}`}>
                                                        {document.tipo}
                                                    </span>
                                                </div>

                                                <div className="flex items-center space-x-2">
                                                    <CalendarIcon className="h-4 w-4" />
                                                    <span>
                                                        {document.fechaCaducidad ?
                                                            new Date(document.fechaCaducidad).toLocaleDateString('es-ES', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric'
                                                            }) :
                                                            'Sin fecha de caducidad'
                                                        }
                                                        {isExpired(document.fechaCaducidad) && (
                                                            <span className="text-red-500 ml-2">(Expirado)</span>
                                                        )}
                                                    </span>
                                                </div>

                                                <div className="flex items-center space-x-2">
                                                    <BuildingOfficeIcon className="h-4 w-4" />
                                                    <span>
                                                        {document.edificios.length > 0 ?
                                                            document.edificios.map(e => e.nombre).join(', ') :
                                                            'Todos los edificios'
                                                        }
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="text-xs text-gray-500">
                                                <span>Archivo: {document.archivo.nombreOriginal}</span>
                                                <span className="mx-2">•</span>
                                                <span>Tamaño: {formatFileSize(document.archivo.tamaño)}</span>
                                                <span className="mx-2">•</span>
                                                <span>Subido: {new Date(document.createdAt).toLocaleDateString('es-ES', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-2 ml-4">
                                            <button
                                                onClick={() => handleDownload(document._id, document.archivo.nombreOriginal)}
                                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                                            >
                                                <ArrowDownTrayIcon className="h-4 w-4" />
                                                <span>Descargar</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default Documents;
